import { useEffect, useRef, useState } from 'react';
import * as PIXI from 'pixi.js';
import './Game.css';
import {
  SYMBOL_ALIASES,
  REELS,
  VISIBLE_ROWS,
  SYMBOL_SIZE,
  REEL_WIDTH,
  BASE_URL,
  SPIN_ICON,
  SPIN_SOUND,
  STOP_SOUND,
  CLICK_SOUND,
  BG_MUSIC,
  AUTOSPIN_ICON,
  AUTOSPIN_ROUNDS,
  WIN_EFFECT_IMAGES,
  PAYLINES,
  PAYTABLE,
  SOUND_ON_ICON,
  SOUND_OFF_ICON,
  WILD_ID,
  SCATTER_ID,
  WRAPPER_ID,
  BET_OPTIONS,
  FEATURE_TOGGLES,
  getPaytableLabel,
  NO_WIN_REROLL_CHANCE,
} from './config';
import { createSymbolKey, evalLinesAdjacent, evalLinesScattered, countScatters } from './logic/winLogic';
import { buildWeights, createPickSymbol, createGridGenerator } from './logic/spinLogic';
import SlotLayout from './components/SlotLayout';
import './gameFonts.css';
import soundConfig from './soundConfig.json';

function GamePage() {
  // Core refs/state that power PIXI, audio, and UI.
  const stageRef = useRef(null);
  const appRef = useRef(null);
  const reelsRef = useRef([]);
  const tweeningRef = useRef([]);
  const assetsReadyRef = useRef(false);
  const audioRef = useRef({});
  const [status, setStatus] = useState('Loading assets…');
  const [balance, setBalance] = useState(1000);
  const [bet, setBet] = useState(10);
  const [isSpinning, setIsSpinning] = useState(false);
  const betRef = useRef(bet);
  const balanceRef = useRef(balance);
  const [betPickerOpen, setBetPickerOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoRounds, setAutoRounds] = useState(0);
  const autoRoundsRef = useRef(0);
  const [autoRemaining, setAutoRemaining] = useState(0);
  const [soundOn, setSoundOn] = useState(true);
  const soundOnRef = useRef(true);
  const bgmRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [paytableOpen, setPaytableOpen] = useState(false);
  const [devOpen, setDevOpen] = useState(false);
  const [devForceWin, setDevForceWin] = useState(false);
  const [devForceLine, setDevForceLine] = useState(2);
  const [devForceWrapper, setDevForceWrapper] = useState(false);
  const [lastWin, setLastWin] = useState(null);
  const [winHighlights, setWinHighlights] = useState([]);
  const [winDisplay, setWinDisplay] = useState([]);
  const [devShowLines, setDevShowLines] = useState(false);
  const devForceWinRef = useRef(false);
  const devForceLineRef = useRef(2);
  const paytableMapRef = useRef({});
  const windowSnapshotRef = useRef(() => []);
  const finalGridRef = useRef(null);
  const inBonusRef = useRef(false);
  const freeSpinsRef = useRef(0);
  const fsMultiplierRef = useRef(1);
  const [inBonus, setInBonus] = useState(false);
  const [freeSpins, setFreeSpins] = useState(0);
  const [fsMultiplier, setFsMultiplier] = useState(1);
  const featureStateRef = useRef({ stickyCols: new Map(), stickyPersistence: new Map(), inWrapper: false });
  const regularSymbolsRef = useRef([]);
  const amountFmt = useRef(
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );
  const MENU_ICON = `${BASE_URL}img/menubutton.webp`;

  const defaultSoundConfig = useRef({
    click: { file: CLICK_SOUND, volume: 80, enabled: true },
    spin: { file: SPIN_SOUND, volume: 90, enabled: true },
    stop: { file: STOP_SOUND, volume: 90, enabled: true },
    music: { file: BG_MUSIC, volume: 50, enabled: true },
  });

  const clampVolume = (value) => {
    const v = Number(value);
    const clamped = Number.isFinite(v) ? Math.min(100, Math.max(1, v)) : 100;
    return clamped / 100;
  };

  const getSoundConfig = (key) => {
    const base = defaultSoundConfig.current[key] || {};
    const override = soundConfig?.[key] || {};
    return { ...base, ...override };
  };

  const createAudioFromConfig = (key, loop = false) => {
    const cfg = getSoundConfig(key);
    if (!cfg.enabled) return null;
    const cacheBustedUrl = `${BASE_URL}sounds/${cfg.file}?v=${Date.now()}`;
    const audio = new Audio(cacheBustedUrl);
    audio.preload = 'auto';
    audio.volume = clampVolume(cfg.volume);
    audio.muted = !soundOnRef.current;
    audio.loop = loop;
    return audio;
  };

  const playSound = (key) => {
    const cfg = getSoundConfig(key);
    if (!cfg.enabled) return;
    const audio = key === 'music' ? bgmRef.current : audioRef.current[key];
    if (!audio) return;
    audio.volume = clampVolume(cfg.volume);
    try {
      if (!audio.loop) {
        audio.currentTime = 0;
      }
      audio.play?.();
    } catch {}
  };

  // Keep refs in sync with latest state.
  useEffect(() => { betRef.current = bet; }, [bet]);
  useEffect(() => { balanceRef.current = balance; }, [balance]);
  useEffect(() => {
    inBonusRef.current = inBonus;
    freeSpinsRef.current = freeSpins;
    fsMultiplierRef.current = fsMultiplier;
  }, [inBonus, freeSpins, fsMultiplier]);
  useEffect(() => { devForceWinRef.current = devForceWin; }, [devForceWin]);
  useEffect(() => { devForceLineRef.current = devForceLine; }, [devForceLine]);
  useEffect(() => { featureStateRef.current.forceWrapper = devForceWrapper; }, [devForceWrapper]);

  // Simple dev toggle (press "d" to show the dev panel).
  useEffect(() => {
    const handler = (e) => {
      if ((e.key || '').toLowerCase() === 'd') setDevOpen((prev) => !prev);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Animate win amounts counting up.
  useEffect(() => {
    if (!winHighlights || !winHighlights.length) {
      setWinDisplay([]);
      return;
    }
    let raf;
    const start = performance.now();
    const duration = 900;
    const tick = () => {
      const now = performance.now();
      const t = Math.min(1, (now - start) / duration);
      setWinDisplay(winHighlights.map((w) => ({ ...w, current: w.lineWin * t })));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [winHighlights]);

  // Sync audio mute state.
  useEffect(() => {
    Object.values(audioRef.current || {}).forEach((a) => {
      if (a && typeof a.muted !== 'undefined') {
        a.muted = !soundOn;
      }
    });
    if (bgmRef.current) {
      bgmRef.current.muted = !soundOn;
      if (soundOn) {
        bgmRef.current.play().catch(() => {});
      } else {
        bgmRef.current.pause();
      }
    }
    soundOnRef.current = soundOn;
  }, [soundOn]);

  // PIXI boot + spin logic.
  useEffect(() => {
    const init = async () => {
      if (appRef.current) return;
      try {
        setStatus('Loading assets…');
        if (!assetsReadyRef.current) {
          await PIXI.Assets.init({
            manifest: `${BASE_URL}manifest.json`,
            basePath: BASE_URL,
          });
          await PIXI.Assets.loadBundle(['img']);
          if (WIN_EFFECT_IMAGES.length) {
            await PIXI.Assets.load(WIN_EFFECT_IMAGES);
          }
          assetsReadyRef.current = true;
        }

        // Texture helpers.
        const getTexture = (alias) => (
            PIXI.Assets.get(alias) ||
            PIXI.Assets.cache.get(alias) ||
            PIXI.Assets.get(`img/${alias}.png`) ||
            PIXI.Assets.get(`img/${alias}`) ||
            null
        );

        const textures = SYMBOL_ALIASES.map((alias) => {
          const tex = getTexture(alias);
          if (!tex) console.warn('Missing texture for alias', alias);
          return { alias, tex };
        }).filter((t) => !!t.tex);
        if (!textures.length) {
          setStatus('No textures found.');
          return;
        }

        audioRef.current = {
          click: createAudioFromConfig('click'),
          spin: createAudioFromConfig('spin'),
          stop: createAudioFromConfig('stop'),
        };
        bgmRef.current = createAudioFromConfig('music', true);

        const app = new PIXI.Application({
          resizeTo: stageRef.current || window,
          backgroundAlpha: 0,
          clearBeforeRender: true,
        });
        stageRef.current.innerHTML = '';
        stageRef.current.appendChild(app.view);

        window.addEventListener('pointerdown', () => bgmRef.current?.play().catch(() => {}), { once: true });

        const reelContainer = new PIXI.Container();
        app.stage.addChild(reelContainer);
        const effectLayer = new PIXI.Container();
        app.stage.sortableChildren = true;
        reelContainer.zIndex = 1;
        effectLayer.zIndex = 10;
        app.stage.addChild(effectLayer);
        const linePalette = [0xff4fd8];

        // Paytable lookup map.
        const keyFromAlias = (alias) => {
          const m = alias.match(/paytable_(high_\d|low_\d)/);
          if (!m) return null;
          return `paytable_${m[1]}`;
        };
        paytableMapRef.current = PAYTABLE.reduce((acc, item) => {
          acc[item.id] = item.payouts || {};
          return acc;
        }, {});
        paytableMapRef.current._keyFromAlias = keyFromAlias;
        regularSymbolsRef.current = PAYTABLE.filter(
            (p) => ![WILD_ID, SCATTER_ID, WRAPPER_ID].includes(p.id)
        ).map((p) => p.id);

        // Snapshot util used by dev panel.
        windowSnapshotRef.current = () =>
            reelsRef.current.map((reel) => {
              const base = Math.floor(reel.position);
              const len = reel.symbols.length;
              return Array.from({ length: VISIBLE_ROWS }).map((_, row) => {
                let idx = (row + 1 - base) % len;
                if (idx < 0) idx += len;
                const sprite = reel.symbols[idx];
                return { alias: sprite?._alias, idx, row };
              });
            });

        // Placeholder textures when assets are missing.
        const ensurePlaceholder = (id, color, text) => {
          const existing = getTexture(id);
          if (existing) return existing;
          if (PIXI.Assets.cache.has(id)) return PIXI.Assets.cache.get(id);
          const g = new PIXI.Graphics();
          g.beginFill(color);
          g.drawRoundedRect(0, 0, SYMBOL_SIZE, SYMBOL_SIZE, 12);
          g.endFill();
          const label = new PIXI.Text(text, {
            fill: '#ffffff',
            fontSize: 18,
            fontWeight: '800',
            align: 'center',
            wordWrap: true,
            wordWrapWidth: SYMBOL_SIZE - 20,
          });
          label.anchor.set(0.5);
          label.x = SYMBOL_SIZE / 2;
          label.y = SYMBOL_SIZE / 2;
          g.addChild(label);
          const tex = app.renderer.generateTexture(g);
          PIXI.Assets.cache.set(id, tex);
          return tex;
        };

        ensurePlaceholder(WILD_ID, 0x2ecc71, 'WILD');
        ensurePlaceholder(SCATTER_ID, 0xe67e22, 'SCATTER');
        ensurePlaceholder(WRAPPER_ID, 0x9b59b6, 'WRAPPER STACKS');

        // Reel construction.
        const reels = [];
        for (let i = 0; i < REELS; i++) {
          const rc = new PIXI.Container();
          rc.x = i * REEL_WIDTH;
          reelContainer.addChild(rc);
          const reel = {
            container: rc,
            symbols: [],
            position: 0,
            previousPosition: 0,
            targetPosition: null,
            blur: new PIXI.BlurFilter(),
          };
          reel.blur.blurX = 0;
          reel.blur.blurY = 0;
          rc.filters = [reel.blur];

          const symbolRows = VISIBLE_ROWS + 1;
          for (let j = 0; j < symbolRows; j++) {
            const sym = textures[Math.floor(Math.random() * textures.length)];
            const sprite = new PIXI.Sprite(sym.tex);
            sprite._alias = sym.alias;
            sprite.y = j * SYMBOL_SIZE;
            sprite.scale.x = sprite.scale.y = Math.min(SYMBOL_SIZE / sprite.width, SYMBOL_SIZE / sprite.height);
            sprite.x = Math.round((SYMBOL_SIZE - sprite.width) / 2);
            reel.symbols.push(sprite);
            rc.addChild(sprite);
          }
          reels.push(reel);
        }
        reelsRef.current = reels;

        const reelMask = new PIXI.Graphics();
        reelMask.beginFill(0xffffff);
        reelMask.drawRect(0, 0, REELS * REEL_WIDTH, VISIBLE_ROWS * SYMBOL_SIZE);
        reelMask.endFill();
        reelContainer.addChild(reelMask);
        reelContainer.mask = reelMask;

        const computeLayout = () => {
          const viewportW = app.screen.width;
          const viewportH = app.screen.height;
          const desiredWidth = REELS * REEL_WIDTH;
          const desiredHeight = VISIBLE_ROWS * SYMBOL_SIZE;
          const baseScale = Math.min((viewportW - 80) / desiredWidth, (viewportH - 80) / desiredHeight);
          const mobileBoost = viewportW <= 640 ? 1.2 : 1;
          const scale = baseScale * mobileBoost;
          reelContainer.scale.set(scale);
          const contentW = desiredWidth * scale;
          const contentH = desiredHeight * scale;
          reelContainer.x = (viewportW - contentW) / 2;
          reelContainer.y = (viewportH - contentH) / 2;
        };
        computeLayout();
        app.renderer.on('resize', () => computeLayout());

        // Tween helpers.
        const tweening = tweeningRef.current;
        const lerp = (a1, a2, t) => a1 * (1 - t) + a2 * t;
        const backout = (amount) => (t) => --t * t * ((amount + 1) * t + amount) + 1;
        const tweenTo = (object, property, target, time, easing, onchange, oncomplete) => {
          const tween = {
            object,
            property,
            propertyBeginValue: object[property],
            target,
            easing,
            time,
            change: onchange,
            complete: oncomplete,
            start: Date.now(),
          };
          tweening.push(tween);
          return tween;
        };

        let activeWinGraphics = [];
        const clearWinLines = () => {
          activeWinGraphics.forEach((obj) => {
            effectLayer.removeChild(obj);
            obj.destroy?.(true);
          });
          activeWinGraphics = [];
        };

        const showWinLines = (lineEntries, persistent = false) => {
          if (!persistent) clearWinLines();
          const entries = (lineEntries || []).map((entry) =>
              typeof entry === 'number'
                  ? { line: entry }
                  : { line: entry.line ?? entry, coords: entry.coords, label: entry.label }
          );
          entries.forEach((item, i) => {
            const lineIdx = item.line ?? 0;
            const line = PAYLINES[lineIdx] || PAYLINES[0];
            const color = linePalette[i % linePalette.length];
            const g = new PIXI.Graphics();
            const scale = reelContainer.scale.x;
            const x0 = reelContainer.x;
            const y0 = reelContainer.y;
            const coords = item.coords && item.coords.length ? item.coords : line.map((row, col) => ({ row, col }));
            const pts = coords.map(({ row, col }) => ({
              x: x0 + col * REEL_WIDTH * scale + (REEL_WIDTH * scale) / 2,
              y: y0 + row * SYMBOL_SIZE * scale + (SYMBOL_SIZE * scale) / 2,
            }));
            g.lineStyle({ width: 14, color, alpha: 0.95, join: 'round', cap: 'round' });
            g.moveTo(pts[0].x, pts[0].y);
            pts.slice(1).forEach((p) => g.lineTo(p.x, p.y));
            g.lineStyle({ width: 26, color, alpha: 0.28, join: 'round', cap: 'round' });
            g.moveTo(pts[0].x, pts[0].y);
            pts.slice(1).forEach((p) => g.lineTo(p.x, p.y));
            pts.forEach((p, idx) => {
              const marker = new PIXI.Graphics();
              marker.beginFill(color, 0.9);
              marker.drawCircle(0, 0, 16);
              marker.endFill();
              marker.lineStyle({ width: 4, color: 0xffffff, alpha: 0.9 });
              marker.drawCircle(0, 0, 20);
              marker.x = p.x;
              marker.y = p.y;
              marker.alpha = 0;
              effectLayer.addChild(marker);
              const labelText = item.label ? item.label : `L${lineIdx + 1}`;
              const label = new PIXI.Text(labelText, {
                fill: '#ffffff',
                fontSize: 14,
                fontWeight: '700',
                align: 'center',
                stroke: '#000000',
                strokeThickness: 3,
              });
              label.anchor.set(0.5);
              label.x = p.x;
              label.y = p.y;
              label.alpha = 0;
              effectLayer.addChild(label);
              const dStart = Date.now();
              const pulse = () => {
                if (!marker || marker.destroyed || !marker.parent) {
                  app.ticker.remove(pulse);
                  return;
                }
                const t = Date.now() - dStart;
                const period = 900;
                const phase = (t % period) / period;
                marker.alpha = 0.3 + 0.7 * Math.sin(phase * Math.PI);
                marker.scale.set(0.95 + 0.1 * Math.sin(phase * Math.PI));
                label.alpha = marker.alpha;
              };
              app.ticker.add(pulse);
              if (!persistent) {
                setTimeout(() => {
                  app.ticker.remove(pulse);
                  effectLayer.removeChild(marker);
                  effectLayer.removeChild(label);
                  marker.destroy(true);
                  label.destroy(true);
                }, 1800 + idx * 80);
              } else {
                activeWinGraphics.push(marker, label);
              }
            });
            g.alpha = persistent ? 1 : 0;
            effectLayer.addChild(g);
            if (!persistent) {
              const fadeIn = 180;
              const hold = 1000;
              const fadeOut = 500;
              const started = Date.now();
              const tick = () => {
                const t = Date.now() - started;
                if (t < fadeIn) {
                  g.alpha = t / fadeIn;
                } else if (t < fadeIn + hold) {
                  g.alpha = 1;
                } else {
                  const ft = Math.min(1, (t - fadeIn - hold) / fadeOut);
                  g.alpha = 1 - ft;
                  if (ft >= 1) {
                    effectLayer.removeChild(g);
                    g.destroy(true);
                    app.ticker.remove(tick);
                  }
                }
              };
              app.ticker.add(tick);
            } else {
              activeWinGraphics.push(g);
            }
          });
        };

        appRef.current = app;
        appRef.current.showWinLines = showWinLines;
        appRef.current.showAllLines = () => showWinLines(PAYLINES.map((_, idx) => idx));
        appRef.current.clearWinLines = clearWinLines;

        // Dim non-winning symbols when a win occurs.
        const applyWinDimming = (wins) => {
          const hasWins = Array.isArray(wins) && wins.length > 0;
          const winSet = new Set();
          if (hasWins) {
            wins.forEach((w) => (w.coords || []).forEach(({ col, row }) => winSet.add(`${col}-${row}`)));
          }
          reelsRef.current.forEach((reel, col) => {
            const base = Math.floor(reel.position);
            const len = reel.symbols.length;
            for (let row = 0; row < VISIBLE_ROWS; row++) {
              let idx = (row + 1 - base) % len;
              if (idx < 0) idx += len;
              const sprite = reel.symbols[idx];
              const hit = hasWins && winSet.has(`${col}-${row}`);
              sprite.alpha = hasWins ? (hit ? 1 : 0.35) : 1;
            }
          });
        };

        // Main ticker.
        app.ticker.add(() => {
          for (let i = 0; i < reels.length; i++) {
            const r = reels[i];
            r.blur.blurY = (r.position - r.previousPosition) * 8;
            r.previousPosition = r.position;
            for (let j = 0; j < r.symbols.length; j++) {
              const s = r.symbols[j];
              const prevy = s.y;
              // Symbol render logic depends on r.position.
              // If r.position is not an integer, s.y will be offset (e.g. 50.5px).
              s.y = ((r.position + j) % r.symbols.length) * SYMBOL_SIZE - SYMBOL_SIZE;

              if (s.y < 0 && prevy > SYMBOL_SIZE) {
                let alias = null;
                // Pre-seed symbols that will be visible at stop so they glide in naturally.
                if (r.targetPosition !== null && finalGridRef.current) {
                  const targetBase = Math.floor(r.targetPosition);
                  const targetRow = (j + targetBase - 1 + r.symbols.length) % r.symbols.length;
                  if (targetRow >= 0 && targetRow < VISIBLE_ROWS) {
                    alias = finalGridRef.current[i][targetRow];
                  }
                }
                if (!alias) {
                  const randSym = textures[Math.floor(Math.random() * textures.length)];
                  alias = randSym.alias;
                }
                const tex = getTexture(alias);
                if (tex) {
                  s.texture = tex;
                  s._alias = alias;
                  s.scale.x = s.scale.y = Math.min(SYMBOL_SIZE / s.texture.width, SYMBOL_SIZE / s.texture.height);
                  s.x = Math.round((SYMBOL_SIZE - s.width) / 2);
                }
              }
            }
          }

          // Tweening update.
          const now = Date.now();
          const remove = [];
          for (let i = 0; i < tweening.length; i++) {
            const t = tweening[i];
            const phase = Math.min(1, (now - t.start) / t.time);
            t.object[t.property] = lerp(t.propertyBeginValue, t.target, t.easing(phase));
            if (t.change) t.change(t);
            if (phase === 1) {
              t.object[t.property] = t.target;
              if (t.complete) t.complete(t);
              remove.push(t);
            }
          }
          for (let i = 0; i < remove.length; i++) {
            const idx = tweening.indexOf(remove[i]);
            if (idx !== -1) tweening.splice(idx, 1);
          }
        });

        // Spin flow.
        const spinOnce = (auto = false) => {
          if (tweening.length) return;
          clearWinLines();
          applyWinDimming([]);
          setWinHighlights([]);
          setWinDisplay([]);
          setIsSpinning(true);
          setStatus('Spinning…');
          playSound('spin');

          const regularSymbols = regularSymbolsRef.current;
          const specials = [];
          if (FEATURE_TOGGLES.wild) specials.push(WILD_ID);
          if (FEATURE_TOGGLES.scatter) specials.push(SCATTER_ID);
          if (FEATURE_TOGGLES.wrapper) specials.push(WRAPPER_ID);
          const allSymbols = [...regularSymbols, ...specials];
          const weights = buildWeights(allSymbols, {
            low: {
              ...(FEATURE_TOGGLES.scatter ? { [SCATTER_ID]: 1 } : {}),
              ...(FEATURE_TOGGLES.wrapper ? { [WRAPPER_ID]: 1 } : {}),
              ...(FEATURE_TOGGLES.wild ? { [WILD_ID]: 2 } : {}),
            },
            default: 8,
          });
          const pickSymbol = createPickSymbol(weights, regularSymbols[0]);
          const generateGrid = createGridGenerator({
            reels: REELS,
            rows: VISIBLE_ROWS,
            pickSymbol,
            wrapperId: FEATURE_TOGGLES.wrapper ? WRAPPER_ID : null,
          });

          // 1. Logical grid
          const stickyColsRaw = new Map(featureStateRef.current.stickyCols || []);
          const stickyPersistenceRaw = new Map(featureStateRef.current.stickyPersistence || []);
          const stickyCols = new Map();
          const stickyPersistenceNext = new Map();
          stickyPersistenceRaw.forEach((val, key) => {
            if (val > 0 && stickyColsRaw.has(key)) {
              stickyCols.set(key, stickyColsRaw.get(key));
              const dec = val - 1;
              if (dec > 0) stickyPersistenceNext.set(key, dec);
            }
          });
          featureStateRef.current = {
            ...featureStateRef.current,
            stickyCols,
            stickyPersistence: stickyPersistenceNext,
          };

          const stickyMask = new Set(); // no per-cell mask; we lock per-column below.
          let grid = generateGrid(stickyMask);
          // Apply sticky columns: force entire column to stored symbol.
          if (stickyCols.size) {
            grid = grid.map((colArr, ci) => {
              if (stickyCols.has(ci)) {
                const sym = stickyCols.get(ci);
                return Array.from({ length: VISIBLE_ROWS }).map(() => sym);
              }
              return colArr;
            });
          }

          const forcedWin = devForceWinRef.current;
          if (forcedWin) {
            const forceIdx = Math.min(PAYLINES.length - 1, Math.max(0, devForceLineRef.current || 0));
            const line = PAYLINES[forceIdx];
            const symbol = 'paytable_high_1';
            line.forEach((row, col) => { grid[col][row] = symbol; });
            devForceWinRef.current = false;
            setDevForceWin(false);
          }
          // Force a wrapper on column 0 for debug if enabled.
          const forcedWrapper = featureStateRef.current.forceWrapper;
          if (forcedWrapper) {
            grid[0] = Array.from({ length: VISIBLE_ROWS }).map(() => WRAPPER_ID);
            featureStateRef.current.forceWrapper = false;
            setDevForceWrapper(false);
          }

          const maybeRerollNoWin = () => {
            if (forcedWin || forcedWrapper) return;
            if (NO_WIN_REROLL_CHANCE <= 0) return;
            const paytableMap = paytableMapRef.current;
            if (!paytableMap || !paytableMap._keyFromAlias) return;
            const symbolKey = createSymbolKey(paytableMap);
            const baseWins = evalLinesAdjacent({
              grid,
              payLines: PAYLINES,
              paytableMap,
              bet: 1,
              multiplier: 1,
              symbolKey,
            });
            if (baseWins.length) return;
            if (Math.random() >= NO_WIN_REROLL_CHANCE) return;
            let reroll = generateGrid(stickyMask);
            if (stickyCols.size) {
              reroll = reroll.map((colArr, ci) => {
                if (stickyCols.has(ci)) {
                  const sym = stickyCols.get(ci);
                  return Array.from({ length: VISIBLE_ROWS }).map(() => sym);
                }
                return colArr;
              });
            }
            grid = reroll;
          };
          maybeRerollNoWin();

          finalGridRef.current = grid;

          // Helper to instantly repaint sprites to match given grid.
          const updateVisualsInstant = (currentGrid) => {
            reelsRef.current.forEach((reel, col) => {
              const base = Math.floor(reel.position);
              const len = reel.symbols.length;
              for (let row = 0; row < VISIBLE_ROWS; row++) {
                const alias = currentGrid[col][row];
                let idx = (row + 1 - base) % len;
                if (idx < 0) idx += len;
                const sprite = reel.symbols[idx];
                const tex = getTexture(alias);
                if (tex) {
                  sprite.texture = tex;
                  sprite._alias = alias;
                  sprite.scale.x = sprite.scale.y = Math.min(SYMBOL_SIZE / sprite.texture.width, SYMBOL_SIZE / sprite.texture.height);
                  sprite.x = Math.round((SYMBOL_SIZE - sprite.width) / 2);
                } else {
                  console.error(`Could not update visual for ${alias} at Col ${col} Row ${row}`);
                }
              }
            });
          };

          // Snap all reels to integer positions before starting to kill float drift between spins.
          reels.forEach((r) => {
            const snap = Math.round(r.position);
            r.position = snap;
            r.previousPosition = snap;
          });

          const finalizeWin = () => {
            const paytableMap = paytableMapRef.current;
            const symbolKey = createSymbolKey(paytableMap);
            let multiplier = fsMultiplierRef.current;
            if (inBonusRef.current) {
              multiplier += 1;
              fsMultiplierRef.current = multiplier;
              setFsMultiplier(multiplier);
            }

            let winsAll = [];
            let totalWin = 0;
            let revealWinsLog = [];

            if (FEATURE_TOGGLES.wrapper) {
              const colsWithWrapper = new Set();
              grid.forEach((colArr, ci) => {
                if (colArr.some((cell) => cell === WRAPPER_ID)) colsWithWrapper.add(ci);
              });
              if (colsWithWrapper.size > 0) {
                const stickyColsNext = new Map(featureStateRef.current.stickyCols || []);
                const stickyPersistNext = new Map(featureStateRef.current.stickyPersistence || []);
                colsWithWrapper.forEach((ci) => {
                  const revealSymbol = regularSymbols[Math.floor(Math.random() * regularSymbols.length)];
                  grid[ci] = Array.from({ length: VISIBLE_ROWS }).map(() => revealSymbol);
                  stickyColsNext.set(ci, revealSymbol);
                  // Persist for at least one additional spin beyond this one.
                  stickyPersistNext.set(ci, Math.max(1, (stickyPersistNext.get(ci) || 0)));
                  freeSpinsRef.current += 1;
                  setFreeSpins(freeSpinsRef.current);
                  inBonusRef.current = true;
                  setInBonus(true);
                });
                // Ensure all locked columns (old + new) get at least one extra spin when any wrapper hits.
                stickyColsNext.forEach((_, ci) => {
                  stickyPersistNext.set(ci, Math.max(1, stickyPersistNext.get(ci) || 0));
                });
                featureStateRef.current = { ...featureStateRef.current, stickyCols: stickyColsNext, stickyPersistence: stickyPersistNext, inWrapper: false };
                finalGridRef.current = grid;
                updateVisualsInstant(grid);
              }
            } else {
              featureStateRef.current = { stickyCols: new Map(), stickyPersistence: new Map(), inWrapper: false };
            }

            const visualRows = windowSnapshotRef.current ? windowSnapshotRef.current() : [];
            const reelPositions = reelsRef.current.map((reel) => ({
              position: reel.position,
              snapped: Math.round(reel.position),
              previous: reel.previousPosition,
              target: reel.targetPosition,
            }));
            console.log('REEL ALIGN DEBUG', { reelPositions, visualRows, logicGrid: grid });

            const baseWins = evalLinesAdjacent({
              grid,
              payLines: PAYLINES,
              paytableMap,
              bet: betRef.current,
              multiplier,
              symbolKey,
            });
                const baseWinsLabeled = baseWins.map((w) => ({
                  ...w,
                  label: getPaytableLabel(w.key),
                }));
                winsAll = winsAll.concat(baseWinsLabeled);
                totalWin += baseWinsLabeled.reduce((s, w) => s + w.lineWin, 0);
                console.log('WIN DEBUG', { baseWins: baseWinsLabeled, revealWins: revealWinsLog, winsAll, totalWin, grid });
                applyWinDimming(winsAll);
                setWinHighlights(winsAll);

                const scatters = FEATURE_TOGGLES.scatter ? countScatters(grid) : 0;
                if (FEATURE_TOGGLES.scatter && scatters >= 3 && !inBonusRef.current) {
                  const fsAward = scatters === 3 ? 6 : scatters === 4 ? 9 : 12;
                  freeSpinsRef.current = fsAward;
              setFreeSpins(fsAward);
              inBonusRef.current = true;
              setInBonus(true);
              fsMultiplierRef.current = 1;
              setFsMultiplier(1);
            } else if (inBonusRef.current) {
              const remaining = Math.max(0, freeSpinsRef.current - 1);
              freeSpinsRef.current = remaining;
              setFreeSpins(remaining);
              if (remaining === 0) {
                inBonusRef.current = false;
                setInBonus(false);
                fsMultiplierRef.current = 1;
                setFsMultiplier(1);
              }
            }

                setBalance((b) => b - betRef.current + totalWin);
                setStatus(totalWin > 0 ? `Win ${totalWin}` : 'No win');
                if (totalWin > 0 && winsAll.length > 0) {
                  setLastWin({
                    amount: totalWin,
                    lines: winsAll.map((w) => w.line),
                    time: new Date().toLocaleTimeString(),
                  });
                  setWinHighlights(winsAll);
                  const linesToShow = winsAll.map((w) => ({
                    line: w.line - 1,
                    coords: w.coords,
                    label: w.label || getPaytableLabel(w.key),
                  }));
                  showWinLines(linesToShow, true);
                } else {
                  setWinHighlights([]);
                }
                setIsSpinning(false);

            if (auto && autoRoundsRef.current > 0) {
              setAutoRemaining((prev) => {
                const next = Math.max(0, prev - 1);
                autoRoundsRef.current = next;
                return next;
              });
              const canContinue = autoRoundsRef.current > 0 && balanceRef.current >= betRef.current;
              if (canContinue) {
                setTimeout(() => spinOnce(true), 400);
              } else {
                autoRoundsRef.current = 0;
                setAutoRemaining(0);
                setStatus(balanceRef.current < betRef.current ? 'Auto play stopped (balance)' : 'Auto play finished');
              }
            }
          };

          let completed = 0;
          const finishReel = () => {
            completed += 1;
            if (completed === REELS) finalizeWin();
          };

          // Animate reels, skipping locked columns.
          reels.forEach((r, i) => {
            const isLocked = featureStateRef.current.stickyCols?.has(i);
            if (isLocked) {
              const base = Math.floor(r.position);
              const len = r.symbols.length;
              for (let row = 0; row < VISIBLE_ROWS; row++) {
                const alias = grid[i][row];
                let idx = (row + 1 - base) % len;
                if (idx < 0) idx += len;
                const sprite = r.symbols[idx];
                const tex = getTexture(alias);
                if (tex) {
                  sprite.texture = tex;
                  sprite._alias = alias;
                  sprite.scale.x = sprite.scale.y = Math.min(SYMBOL_SIZE / sprite.texture.width, SYMBOL_SIZE / sprite.texture.height);
                  sprite.x = Math.round((SYMBOL_SIZE - sprite.width) / 2);
                }
              }
              r.targetPosition = null;
              r.position = Math.round(r.position);
              r.previousPosition = r.position;
              finishReel();
              return;
            }

            const extra = Math.floor(Math.random() * 4);
            const currentPos = Math.round(r.position);
            const delta = (20 + i * 5 + extra) * 4;
            const target = currentPos + delta;
            r.position = currentPos;
            r.previousPosition = currentPos;
            r.targetPosition = target;

            const time = 2000 + i * 300;
            tweenTo(r, 'position', target, time, backout(0.15), null, () => {
              r.position = Math.round(target);
              r.targetPosition = null;
              r.previousPosition = r.position;

              try {
                playSound('stop');
              } catch {}

              const base = Math.floor(r.position);
              const len = r.symbols.length;
              for (let row = 0; row < VISIBLE_ROWS; row++) {
                const alias = grid[i][row];
                let idx = (row + 1 - base) % len;
                if (idx < 0) idx += len;
                const sprite = r.symbols[idx];
                const tex = getTexture(alias);
                if (tex) {
                  sprite.texture = tex;
                  sprite._alias = alias;
                  sprite.scale.x = sprite.scale.y = Math.min(SYMBOL_SIZE / sprite.texture.width, SYMBOL_SIZE / sprite.texture.height);
                  sprite.x = Math.round((SYMBOL_SIZE - sprite.width) / 2);
                }
              }
              finishReel();
            });
          });
        };

        appRef.current.spinOnce = spinOnce;
        setStatus('Ready');
      } catch (err) {
        console.error(err);
        setStatus(`Error loading assets: ${err.message || err}`);
      }
    };

    init();
    return () => {
      if (appRef.current) {
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
      }
    };
  }, []);

  // Auto-show paylines when dev flag is on.
  useEffect(() => {
    if (!devShowLines) return undefined;
    const tick = () => {
      const allLines = PAYLINES.map((_, idx) => idx);
      appRef.current?.showWinLines?.(allLines);
    };
    tick();
    const id = setInterval(tick, 1200);
    return () => clearInterval(id);
  }, [devShowLines]);

  const handleSpin = (auto = false) => {
    if (isSpinning || balance < bet) return;
    appRef.current?.spinOnce?.(auto);
  };

  // UI handlers kept tiny to keep layout file dumb.
  const handleLogWindow = () => {
    const snapshot = windowSnapshotRef.current();
    console.log('Current window snapshot (visible rows)', snapshot);
  };
  const handleSelectBet = (value) => {
    setBet(value);
    setBetPickerOpen(false);
  };
  const handleChangeBet = (value) => {
    setBet(value);
  };
  const handleStartAuto = () => {
    autoRoundsRef.current = autoRounds;
    setAutoRemaining(autoRounds);
    setAutoOpen(false);
    setStatus(`Auto play ${autoRoundsRef.current} rounds`);
    handleSpin(true);
  };
  const handleShowWinFrame = () => appRef.current?.showWinLines?.([Math.floor(VISIBLE_ROWS / 2)]);
  const handleTogglePaylines = () => {
    setDevShowLines((prev) => {
      const next = !prev;
      if (next) appRef.current?.showAllLines?.();
      else appRef.current?.clearWinLines?.();
      return next;
    });
  };

  return (
    <SlotLayout
      stageRef={stageRef}
      baseUrl={BASE_URL}
          balance={balance}
          bet={bet}
          betPickerOpen={betPickerOpen}
          onOpenBetPicker={() => setBetPickerOpen(true)}
          onCloseBetPicker={() => setBetPickerOpen(false)}
          onSelectBet={handleSelectBet}
          onChangeBet={handleChangeBet}
          betOptions={BET_OPTIONS}
          autoOpen={autoOpen}
          onOpenAuto={() => setAutoOpen(true)}
          onCloseAuto={() => setAutoOpen(false)}
          onStartAuto={handleStartAuto}
      autoRounds={autoRounds}
      onSetAutoRounds={setAutoRounds}
      autoRemaining={autoRemaining}
      menuOpen={menuOpen}
      onOpenMenu={() => setMenuOpen(true)}
      onCloseMenu={() => setMenuOpen(false)}
      menuIcon={MENU_ICON}
      onOpenPaytable={() => { setMenuOpen(false); setPaytableOpen(true); }}
      paytableOpen={paytableOpen}
      onClosePaytable={() => setPaytableOpen(false)}
      paytableData={PAYTABLE}
      paylines={PAYLINES}
      devOpen={devOpen}
      status={status}
      onLogWindow={handleLogWindow}
      devForceLine={devForceLine}
      onSetDevForceLine={setDevForceLine}
      onSetDevForceWrapper={setDevForceWrapper}
      onForceWin={() => setDevForceWin(true)}
      devShowLines={devShowLines}
      onTogglePaylines={handleTogglePaylines}
      onShowWinFrame={handleShowWinFrame}
      lastWin={lastWin}
      winHighlights={winHighlights}
      winDisplay={winDisplay}
      formatAmount={(v) => amountFmt.current.format(v || 0)}
      handleSpin={handleSpin}
      isSpinning={isSpinning}
          soundOn={soundOn}
          toggleSound={() => setSoundOn((prev) => !prev)}
          spinIcon={SPIN_ICON}
          autoSpinIcon={AUTOSPIN_ICON}
          soundOnIcon={SOUND_ON_ICON}
          soundOffIcon={SOUND_OFF_ICON}
          autospinRounds={AUTOSPIN_ROUNDS}
      />
  );
}

export default GamePage;
